/**
 * Auth Zustand store — manages user session, org selection, and auth state.
 *
 * Wraps better-auth client calls and persists orgId to localStorage.
 */
import { create } from "zustand";
import { authClient } from "@/lib/auth-client";

interface AuthUser {
  id: string;
  name: string;
  email: string;
  image?: string;
}

interface AuthOrg {
  id: string;
  name: string;
  slug: string;
}

interface AuthState {
  user: AuthUser | null;
  session: { token: string } | null;
  orgId: string | null;
  orgs: AuthOrg[];
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

interface AuthActions {
  loadSession(): Promise<void>;
  loadOrgs(): Promise<void>;
  login(email: string, password: string): Promise<boolean>;
  signup(name: string, email: string, password: string): Promise<boolean>;
  loginWithGoogle(): Promise<void>;
  loginWithMicrosoft(): Promise<void>;
  logout(): Promise<void>;
  setOrg(orgId: string): void;
}

const STORAGE_KEY = "ajnt_org_id";

function getPersistedOrgId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState & AuthActions>((set, get) => ({
  user: null,
  session: null,
  orgId: getPersistedOrgId(),
  orgs: [],
  isLoading: false,
  isAuthenticated: false,
  error: null,

  async loadSession() {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await authClient.getSession();
      if (error || !data) {
        set({ user: null, session: null, isAuthenticated: false, isLoading: false });
        return;
      }
      set({
        user: data.user as AuthUser,
        session: { token: data.session.token },
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      set({ user: null, session: null, isAuthenticated: false, isLoading: false });
    }
  },

  async loadOrgs() {
    try {
      const { data } = await authClient.organization.list();
      const orgs = (data ?? []) as AuthOrg[];
      set({ orgs });

      // Auto-select first org if none selected
      const { orgId } = get();
      if (!orgId && orgs.length > 0) {
        get().setOrg(orgs[0].id);
      }
    } catch {
      set({ orgs: [] });
    }
  },

  async login(email: string, password: string) {
    set({ isLoading: true, error: null });
    try {
      const { error } = await authClient.signIn.email({ email, password });
      if (error) {
        set({ isLoading: false, error: error.message ?? "Login failed" });
        return false;
      }
      await get().loadSession();
      return true;
    } catch (e) {
      set({ isLoading: false, error: (e as Error).message });
      return false;
    }
  },

  async signup(name: string, email: string, password: string) {
    set({ isLoading: true, error: null });
    try {
      const { error } = await authClient.signUp.email({ name, email, password });
      if (error) {
        set({ isLoading: false, error: error.message ?? "Signup failed" });
        return false;
      }
      await get().loadSession();
      return true;
    } catch (e) {
      set({ isLoading: false, error: (e as Error).message });
      return false;
    }
  },

  async loginWithGoogle() {
    await authClient.signIn.social({
      provider: "google",
      callbackURL: "https://app.ajnt.ai/dashboard",
    });
  },

  async loginWithMicrosoft() {
    await authClient.signIn.social({
      provider: "microsoft",
      callbackURL: "https://app.ajnt.ai/dashboard",
    });
  },

  async logout() {
    await authClient.signOut();
    localStorage.removeItem(STORAGE_KEY);
    set({
      user: null,
      session: null,
      orgId: null,
      orgs: [],
      isAuthenticated: false,
      error: null,
    });
  },

  setOrg(orgId: string) {
    try {
      localStorage.setItem(STORAGE_KEY, orgId);
    } catch {
      /* ignore */
    }
    set({ orgId });
  },
}));
